import { Theme } from '@mui/material';
import MuiButton, { ButtonProps as MuiButtonProps } from '@mui/material/Button';
import MuiIconButton, {
    IconButtonProps as MuiIconButtonProps
} from '@mui/material/IconButton';
import * as React from 'react';

import { Badge, BadgeProps } from '../badge';
import { getIcon, IconProps, IconType } from '../icon';
import { PopoverProps, usePopoverProps } from '../popover';
import { styled } from '../styled';
import { ColorProp, useRubiksTheme } from '../theme';
import { Tooltip, TooltipProps } from '../tooltip';
import { enumType, makeEnum, withVersion } from '../utils';

const iconButtonSizes = ['smaller', 'small', 'medium', 'large'] as const;

export const IconButtonSize = makeEnum(...iconButtonSizes);
export type IconButtonSize = enumType<typeof IconButtonSize>;

const SIZE_CONFIG = {
    smaller: { button: 2.75, icon: 2 },
    small: { button: 4.25, icon: 3 },
    medium: { button: 5, icon: 3 },
    large: { button: 6, icon: 3 }
} satisfies Record<IconButtonSize, { button: number; icon: number }>;

const variants = ['contained', 'text'] as const;

export const IconButtonVariant = makeEnum(...variants);
export type IconButtonVariant = enumType<typeof IconButtonVariant>;

const StyledIconButton = styled(MuiIconButton)<{ $size: IconButtonSize }>(({
    theme,
    $size
}) => {
    const cfg = SIZE_CONFIG[$size];
    return {
        width: theme.spacing(cfg.button),
        height: theme.spacing(cfg.button),
        padding: 0
    };
});

const StyledContainedButton = styled(MuiButton)<{ $size: IconButtonSize }>(({
    theme,
    $size
}) => {
    const cfg = SIZE_CONFIG[$size];
    return {
        width: theme.spacing(cfg.button),
        height: theme.spacing(cfg.button),
        minWidth: 0,
        padding: 0,
        '& [data-role="icon"]': {
            fill: 'currentColor',
            color: 'inherit'
        }
    };
});

interface IconButtonBaseProps
    extends TypedOmit<MuiIconButtonProps, 'size' | 'color'> {
    size?: IconButtonSize;
    icon?: IconType;
    iconProps?: IconProps;
    badge?: BadgeProps;
    tooltip?: React.ReactNode;
    tooltipPlacement?: TooltipProps['placement'];
}

interface ContainedIconButtonProps extends IconButtonBaseProps {
    variant: 'contained';
    color?: MuiButtonProps['color'];
}

interface TextIconButtonProps extends IconButtonBaseProps {
    variant?: 'text';
    color?: MuiIconButtonProps['color'];
}

export type IconButtonProps = ContainedIconButtonProps | TextIconButtonProps;

const IconButtonImpl = React.forwardRef<HTMLButtonElement, IconButtonProps>(
    (props, ref) => {
        const {
            icon,
            badge,
            iconProps: _iconProps,
            tooltip,
            tooltipPlacement,
            size = 'medium',
            variant = 'text',
            color,
            ...muiProps
        } = props;
        const iconColor = getColorProp(color);
        const Icon = icon ? getIcon(icon) : () => null;

        const cfg = SIZE_CONFIG[size];
        const iconSizeSx = {
            fontSize: (theme: Theme) => theme.spacing(cfg.icon)
        };
        const iconProps: IconProps = _iconProps?.fontSize
            ? _iconProps
            : {
                  ..._iconProps,
                  sx: _iconProps?.sx
                      ? [
                            iconSizeSx,
                            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                            ...(Array.isArray(_iconProps.sx)
                                ? _iconProps.sx
                                : [_iconProps.sx])
                        ]
                      : iconSizeSx
              };

        const content = (
            <Badge {...badge}>
                {muiProps.children ?? <Icon color={iconColor} {...iconProps} />}
            </Badge>
        );

        return (
            <Tooltip
                bypass={!tooltip}
                title={tooltip}
                placement={tooltipPlacement}
            >
                {variant === 'contained' ? (
                    <StyledContainedButton
                        ref={ref}
                        data-role="icon-button"
                        variant="contained"
                        $size={size}
                        disableElevation
                        color={color as MuiButtonProps['color']}
                        {...muiProps}
                    >
                        {content}
                    </StyledContainedButton>
                ) : (
                    <StyledIconButton
                        ref={ref}
                        data-role="icon-button"
                        $size={size}
                        color={color}
                        {...muiProps}
                    >
                        {content}
                    </StyledIconButton>
                )}
            </Tooltip>
        );
    }
);

export const IconButton = withVersion(IconButtonImpl, '1.2.0');

function getColorProp(
    color: MuiIconButtonProps['color']
): ColorProp | undefined {
    if (color === 'default') {
        return undefined;
    }

    return color;
}

export function useIconButtonPopoverProps(
    props: TypedOmit<IconButtonProps, 'icon'> & {
        icon: IconType;
        'aria-label'?: string;
        'data-role'?: string;
        badge?: BadgeProps;
        theme?: Theme;
        /** @default click */
        behaviour?: PopoverProps['behaviour'];
    }
) {
    const { ThemeProvider } = useRubiksTheme();
    const { theme, behaviour, ...iconButtonProps } = props;

    return usePopoverProps({
        disabled: props.disabled,
        label: ({ open, ...labelProps }) => {
            const color = getColorProp(iconButtonProps.color);
            const button = (
                <IconButton
                    {...(iconButtonProps as IconButtonProps)}
                    iconProps={{
                        color: color ?? (open ? 'primary' : undefined),
                        ...props.iconProps,
                        filled: open
                    }}
                    {...labelProps}
                />
            );

            return theme ? (
                <ThemeProvider theme={theme}>{button}</ThemeProvider>
            ) : (
                button
            );
        },
        behaviour,
        'aria-label': props['aria-label']
    });
}
